import { Component, computed, effect, input, output, signal } from '@angular/core';
import { InputType as QuestionInputType } from '@cureskin/api-client-rn';
import { ArrowIconComponent } from '@components/arrow-icon/arrow-icon.component';
import { AiAssistantChatInput } from '@services/ai-assistant-chat-service/ai-assistant-chat.types';

const MULTI_SELECT_SEPARATOR = ', ';

@Component({
  selector: 'ai-multi-select',
  templateUrl: './ai-multi-select.component.html',
  imports: [ArrowIconComponent],
})
export class AiMultiSelectComponent {
  options = input<AiAssistantChatInput[]>([]);
  submitAnswer = output<string>();

  private readonly selectedIndices = signal<Set<number>>(new Set());
  readonly submitted = signal(false);

  readonly buttonInputs = computed(() => this.options()
    .map((each, originalIndex) => ({ ...each, originalIndex }))
    .filter(each => each.type === QuestionInputType.BUTTON)
    .sort((a, b) => Number(!!a.isExclusive) - Number(!!b.isExclusive)));

  readonly hasSelection = computed(() => this.selectedIndices().size > 0);

  private readonly hasNonExclusiveSelection = computed(() =>
    this.buttonInputs().some(each => this.selectedIndices().has(each.originalIndex) && !each.isExclusive));

  private readonly hasExclusiveSelection = computed(() =>
    this.buttonInputs().some(each => this.selectedIndices().has(each.originalIndex) && !!each.isExclusive));

  readonly isVisible = computed(() => !this.submitted() && this.buttonInputs().length > 0);

  readonly displayInputs = computed(() => {
    const selected = this.selectedIndices();
    const hasNonExclusive = this.hasNonExclusiveSelection();
    const hasExclusive = this.hasExclusiveSelection();
    return this.buttonInputs().map(each => ({
      ...each,
      isSelected: selected.has(each.originalIndex),
      isDimmed: (each.isExclusive && hasNonExclusive) || (!each.isExclusive && hasExclusive),
    }));
  });

  constructor() {
    effect(() => {
      this.options();
      this.selectedIndices.set(new Set());
      this.submitted.set(false);
    });
  }

  toggleOption(originalIndex: number): void {
    const item = this.buttonInputs().find(each => each.originalIndex === originalIndex);
    const current = new Set(this.selectedIndices());
    if (current.has(originalIndex)) {
      current.delete(originalIndex);
    } else {
      if (item?.isExclusive) {
        current.clear();
      } else {
        for (const idx of current) {
          const existing = this.buttonInputs().find(each => each.originalIndex === idx);
          if (existing?.isExclusive) current.delete(idx);
        }
      }
      current.add(originalIndex);
    }
    this.selectedIndices.set(current);
  }

  onSubmit(): void {
    if (!this.hasSelection()) return;
    const message = this.buttonInputs()
      .filter(each => this.selectedIndices().has(each.originalIndex))
      .map(each => each.text ?? '')
      .join(MULTI_SELECT_SEPARATOR);
    this.submitted.set(true);
    this.submitAnswer.emit(message);
  }
}
